import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export const AdminRegistrations = () => {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/registrations");
      setItems(data);
    } catch (e) {
      toast.error("Could not load registrations");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Remove the registration for ${r.name} (${r.email})?`)) return;
    try {
      await api.delete(`/registrations/${r.id}`);
      toast.success("Registration removed");
      setItems((prev) => prev.filter((i) => i.id !== r.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Clear ALL ${items.length} registrations? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/registrations");
      toast.success(`Cleared ${data.removed} registration(s)`);
      setItems([]);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (items === null) return <p className="text-[#525252]">Loading…</p>;

  return (
    <div data-testid="registrations-panel">
      {items.length > 0 && (
        <div className="flex justify-end mb-4">
          <button onClick={clearAll} data-testid="registrations-clear-all"
            className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-white/10 px-4 py-2.5 text-[#737373] hover:text-red-400 hover:border-red-400/40 transition-colors duration-300">
            <Trash2 className="w-3.5 h-3.5" /> Clear All ({items.length})
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-[#A3A3A3]" data-testid="registrations-empty">No registrations yet. Couples and guests who register on the front page will appear here.</p>
      ) : (
        <div className="border border-white/10" data-testid="registrations-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Registered</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Name</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Email</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]">Type</TableHead>
                <TableHead className="text-[#525252] uppercase tracking-[0.2em] text-[10px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} className="border-white/10 hover:bg-white/5" data-testid={`registration-row-${r.id}`}>
                  <TableCell className="text-[#A3A3A3] text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-[#F5F4F0] text-sm">{r.name}</TableCell>
                  <TableCell className="text-[#A3A3A3] text-sm">{r.email}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 border ${r.type === "couple" ? "text-[#C9A26B] border-[#C9A26B]/50" : "text-[#A3A3A3] border-white/20"}`}>
                      {r.type}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => remove(r)} data-testid={`registration-delete-${r.id}`}
                      className="text-[10px] tracking-[0.2em] uppercase text-[#737373] hover:text-red-400 transition-colors duration-300">
                      Remove
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
